import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Input, Table, Statistic, Switch, Badge, Space, Typography } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderContainer = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const IconWrapper = styled.div<{ $bgColor: string; $color: string }>`
  padding: 12px;
  border-radius: 12px;
  background: ${(props) => props.$bgColor};
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HeaderTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  letter-spacing: -0.02em !important;

  @media (max-width: 768px) {
    font-size: 20px !important;
  }
`;

export const HeaderSubtitle = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')} !important;
  margin-top: 4px;
  display: block;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const StatCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.4)', '0 4px 12px rgba(0, 0, 0, 0.12)')};
  }

  .ant-card-body {
    border-radius: 12px;
    padding: 12px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  }
`;

export const StatContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const StatInfo = styled.div`
  flex: 1;
`;

export const StatLabel = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')} !important;
  font-weight: 500;
  display: block;
  margin-bottom: 8px;
`;

export const StatValue = styled(Typography.Text)`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  display: block;
  margin-bottom: 4px;
`;

export const StatHelp = styled(Typography.Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')} !important;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ConfigCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  margin-bottom: 32px;

  .ant-card-head {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
    border-radius: 12px 12px 0 0;
  }

  .ant-card-head-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }

  .ant-card-body {
    border-radius: 12px;
    padding: 12px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  }
`;

export const ConfigHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

export const ConfigHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ToggleContainer = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')};
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  margin-bottom: 24px;
`;

export const ToggleContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ToggleInfo = styled.div`
  flex: 1;
`;

export const ToggleLabel = styled(Typography.Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
  display: block;
  margin-bottom: 4px;
`;

export const ToggleDescription = styled(Typography.Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')} !important;
`;

export const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ConfigItem = styled.div<{ $bgColor: string; $borderColor: string }>`
  padding: 16px;
  background: ${(props) => props.$bgColor};
  border-radius: 12px;
  border: 1px solid ${(props) => props.$borderColor};
`;

export const ConfigItemLabel = styled(Typography.Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
  display: block;
  margin-bottom: 12px;
`;

export const ConfigItemValue = styled(Typography.Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
`;

export const StyledInput = styled(Input)`
  border-radius: 8px;
  height: 40px;
  background: ${(props) => getThemeValue(props, '#2d3748', '#ffffff')} !important;
  border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')} !important;
  color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;

  &:hover,
  &:focus {
    border-color: ${(props) => getThemeValue(props, '#4a5568', '#40a9ff')} !important;
  }
`;

export const DisabledState = styled.div`
  padding: 48px;
  text-align: center;
  background: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')};
  border-radius: 12px;
`;

export const DisabledIcon = styled.div`
  font-size: 32px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')};
  margin-bottom: 16px;
`;

export const DisabledTitle = styled(Typography.Text)`
  font-size: 18px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')} !important;
  display: block;
  margin-bottom: 8px;
`;

export const DisabledDescription = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#718096', '#8c8c8c')} !important;
`;

export const ActionButtons = styled(Space)`
  width: 100%;
  justify-content: flex-end;
  margin-top: 24px;
`;

export const StyledButton = styled(Button)`
  background-color: #2D6CCA !important;
  color: #ffffff !important;
  border-color: #2D6CCA !important;
  font-size: 16px !important;
  border-radius: 8px !important;
  height: 38px;
  font-weight: 500;

  &:hover {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: #ffffff !important;
  }
`;

export const PrimaryButton = styled(StyledButton)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;
  font-size: 16px !important;
  border-radius: 8px !important;

  &:hover {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: #ffffff !important;
  }
`;

export const CancelButton = styled(Button)`
  background-color: #ededed !important;
  color: #262626 !important;
  border-color: #ededed !important;
  font-size: 16px !important;
  border-radius: 8px !important;
  height: 38px;
  font-weight: 500;

  &:hover {
    background-color: #d4d4d4 !important;
    border-color: #d4d4d4 !important;
    color: #262626 !important;
  }

  &:focus {
    background-color: #ededed !important;
    border-color: #ededed !important;
    color: #262626 !important;
  }
`;

export const CustomersCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};

  .ant-card-head {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
    border-radius: 16px 16px 0 0;
  }

  .ant-card-head-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }

  .ant-card-body {
    border-radius: 12px;
    padding: 12px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  }
`;

export const CustomersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const CustomersHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CustomersBadge = styled(Badge)`
  .ant-badge-count {
    background: #14b8a6;
    border-radius: 12px;
    padding: 4px 12px;
    font-size: 12px;
  }
`;

export const StyledTable = styled(Table)`
  .ant-table {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-radius: 12px;
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
    font-weight: 600;
    padding: 16px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
    padding: 16px;
  }

  .ant-table-tbody > tr:hover > td {
    background: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')} !important;
  }

  .ant-table-tbody > tr:nth-child(even) {
    background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')};
  }

  /* Hide expand icon column */
  .ant-table-expand-icon-col {
    display: none !important;
  }

  .ant-table-row-expand-icon-cell {
    display: none !important;
  }
`;

export const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;

  &:hover {
    color: #1890ff;
  }
`;

export const TableRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const IconBadge = styled.div<{ $bgColor: string; $color: string }>`
  padding: 8px;
  border-radius: 50%;
  background: ${(props) => props.$bgColor};
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TransactionCard = styled(Card)`
  /* margin: 16px; */
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.2)', '0 2px 8px rgba(0, 0, 0, 0.06)')};

  .ant-card-body {
    border-radius: 12px;
    padding: 12px;
    background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')};
  }
`;

export const TransactionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const TransactionTable = styled(Table)`
  .ant-table {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
    font-weight: 600;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
  }

  .ant-table-tbody > tr:hover > td {
    background: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')} !important;
  }

  .ant-table-tbody > tr:nth-child(even) {
    background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')};
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
  gap: 16px;
`;

export const LoadingText = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')} !important;
`;

export const PaginationContainer = styled.div`
  width: 100%;
  margin-top: 24px;
`;

// Additional styled components for inline styles
export const HeaderText = styled.span<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? '#fff' : '#1a1a1a')};
  font-weight: 600;
`;

export const CustomerCountBadge = styled.span`
  background-color: #14b8a6;
  color: #fff;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  margin-left: 8px;
`;

export const EditButton = styled(Button)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;
  font-size: 16px !important;
  border-radius: 8px !important;
`;

export const ColumnTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const UserNameText = styled.span<{ $isDarkMode: boolean }>`
  font-weight: 500;
  color: ${(props) => (props.$isDarkMode ? '#fff' : '#262626')};
`;

export const PhoneText = styled.span<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? '#a0aec0' : '#8c8c8c')};
`;

export const TotalSpentText = styled.span<{ $isDarkMode: boolean }>`
  font-weight: 700;
  color: ${(props) => (props.$isDarkMode ? '#fff' : '#262626')};
`;

export const TransactionHistoryText = styled.span<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? '#fff' : '#1a1a1a')};
  font-weight: 600;
  font-size: 16px;
`;

export const DateTimeText = styled.span<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? '#a0aec0' : '#8c8c8c')};
  font-size: 12px;
`;

export const TransactionAmountText = styled.span<{ $isDarkMode: boolean }>`
  font-weight: 500;
  color: ${(props) => (props.$isDarkMode ? '#fff' : '#262626')};
`;

export const ProgramStatusValue = styled.span<{ $isActive: boolean; $isDarkMode: boolean }>`
  color: ${(props) =>
    props.$isActive
      ? props.$isDarkMode
        ? '#22c55e'
        : '#16a34a'
      : props.$isDarkMode
      ? '#a0aec0'
      : '#718096'};
`;

export const ProgramStatusHelp = styled(Typography.Text)<{ $isActive: boolean; $isDarkMode: boolean }>`
  color: ${(props) =>
    props.$isActive
      ? props.$isDarkMode
        ? '#22c55e'
        : '#16a34a'
      : props.$isDarkMode
      ? '#a0aec0'
      : '#718096'} !important;
`;
